// Path: ./src/app/pages/ElasticTrigger/engagement/EngagementRunReport.tsx
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from "@mui/material";
import { EngagementIngestWindowDto } from "@swagger/typescript-fetch-goflyer";
import { EngagementRunOutcome } from "./useEngagementIngest";
import { formatCount, formatWindowRange, matchRate, EMPTY } from "./format";

const RUN_LABEL: Record<EngagementRunOutcome["kind"], string> = {
  ingest: "Run now",
  backfill: "Backfill history",
};

type Props = {
  outcome: EngagementRunOutcome;
  /** Disabled while any run holds the client-side lock. */
  retryDisabled: boolean;
  onRetry: () => void;
  onDismiss: () => void;
};

const WindowsTable = ({ windows }: { windows: EngagementIngestWindowDto[] }) => (
  <Box sx={{ overflowX: "auto" }}>
    <Table size="small" sx={{ minWidth: 720, mt: 1 }}>
      <TableHead>
        <TableRow>
          <TableCell>Window</TableCell>
          <TableCell align="right">Fetched</TableCell>
          <TableCell align="right">Malformed</TableCell>
          <TableCell align="right">Matched</TableCell>
          <TableCell align="right">Unmatched</TableCell>
          <TableCell align="right">Match rate</TableCell>
          <TableCell align="right">Upserted</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {windows.map((window, index) => {
          const rate = matchRate(window.matchedRows, window.unmatchedRows);
          // A rate this low is the signature of a broken join key rather than
          // quiet traffic. The server's own guard
          // (engagement.ingest.min_match_rate_percent) is the authority on
          // whether to abort; this is only a reading aid.
          const poorMatch = rate !== null && rate < 50;
          return (
            <TableRow
              key={`${window.windowStart}-${index}`}
              sx={{
                backgroundColor: window.abortedReason
                  ? (theme) => alpha(theme.palette.error.main, 0.08)
                  : undefined,
              }}
            >
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                {formatWindowRange(window.windowStart, window.windowEnd)}
                {window.abortedReason && (
                  <Chip
                    size="small"
                    color="error"
                    label="Aborted"
                    sx={{ ml: 1 }}
                  />
                )}
                {window.truncated && (
                  <Chip
                    size="small"
                    color="warning"
                    label="Truncated"
                    sx={{ ml: 1 }}
                  />
                )}
              </TableCell>
              <TableCell align="right">
                {formatCount(window.fetchedRows)}
              </TableCell>
              <TableCell align="right">
                {formatCount(window.malformedRows)}
              </TableCell>
              <TableCell align="right">
                {formatCount(window.matchedRows)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  color: poorMatch ? "warning.dark" : undefined,
                  fontWeight: poorMatch ? 600 : undefined,
                }}
              >
                {formatCount(window.unmatchedRows)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  color: poorMatch ? "warning.dark" : undefined,
                  fontWeight: poorMatch ? 600 : undefined,
                }}
              >
                {rate === null ? EMPTY : `${rate}%`}
              </TableCell>
              <TableCell align="right">
                {formatCount(window.upsertedRows)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </Box>
);

export const EngagementRunReport = ({
  outcome,
  retryDisabled,
  onRetry,
  onDismiss,
}: Props) => {
  const label = RUN_LABEL[outcome.kind];

  const dismissAction = (
    <Button color="inherit" size="small" onClick={onDismiss}>
      Dismiss
    </Button>
  );

  const retryAction = (
    <Button
      color="inherit"
      size="small"
      onClick={onRetry}
      disabled={retryDisabled}
    >
      Retry
    </Button>
  );

  if (outcome.error) {
    // A gateway timeout or dropped connection is not a failed run: the server
    // is very likely still working. Say so instead of reporting a stop.
    if (outcome.error.inconclusive) {
      return (
        <Alert severity="warning" action={dismissAction}>
          <AlertTitle>{label} — no response yet</AlertTitle>
          {outcome.error.message} This does <strong>not</strong> mean the run
          stopped — a backfill is synchronous and often outlives the gateway
          timeout. Leave this page open and watch{" "}
          <strong>Earliest bucket</strong> above move backwards; status refreshes
          on its own. Re-running later is safe.
        </Alert>
      );
    }
    return (
      <Alert severity="error" action={dismissAction}>
        <AlertTitle>{label} failed</AlertTitle>
        {outcome.error.message}
      </Alert>
    );
  }

  const result = outcome.result;
  if (!result) return null;

  // `ran: false` means another ingest holds the advisory lock — no work was
  // attempted. It is neither a success nor a failure.
  if (!result.ran) {
    return (
      <Alert severity="info" action={retryAction}>
        <AlertTitle>{label} — skipped, nothing was attempted</AlertTitle>
        {result.message} Another ingest currently holds the lock. Retry in a
        moment.
      </Alert>
    );
  }

  const windows = result.windows ?? [];
  const aborted = windows.filter((window) => window.abortedReason);
  const truncated = windows.filter((window) => window.truncated);

  const severity = aborted.length
    ? "error"
    : truncated.length
      ? "warning"
      : "success";

  const title = aborted.length
    ? `${label} — aborted, nothing was written`
    : truncated.length
      ? `${label} — completed, but the pull was cut short`
      : `${label} — completed`;

  return (
    <Alert severity={severity} action={dismissAction}>
      <AlertTitle>{title}</AlertTitle>
      <Typography variant="body2">{result.message}</Typography>

      {typeof result.pruned === "number" && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Retention prune removed {formatCount(result.pruned)} expired{" "}
          {result.pruned === 1 ? "bucket" : "buckets"}.
        </Typography>
      )}

      {aborted.map((window, index) => (
        <Typography
          key={`aborted-${index}`}
          variant="body2"
          sx={{ mt: 0.5, fontWeight: 600 }}
        >
          Match-rate guard tripped: {window.abortedReason} The PostHog{" "}
          <code>item_id</code> did not match flyer items, so this window wrote
          nothing.
        </Typography>
      ))}

      {!aborted.length && truncated.length > 0 && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {truncated.length === 1 ? "A window" : `${truncated.length} windows`}{" "}
          hit the row cap, so the counts above are understated. Raise{" "}
          <code>engagement.ingest.max_rows</code> in Configuration Management and
          run again.
        </Typography>
      )}

      {windows.length > 0 && <WindowsTable windows={windows} />}

      {windows.some((window) => window.unmatchedRows > 0) && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1 }}
        >
          A low match rate means the join key is broken, not that traffic was
          quiet — unmatched rows are events whose <code>item_id</code> resolved
          to no flyer item.
        </Typography>
      )}
    </Alert>
  );
};
